// src/encounter/lib/to-referral-card.ts
// [PROD-1395] Map the newest open referral row to the words the pre-encounter "Referral" card shows.
// PURE. Every value is returned as plain text: the card draws it as text, never as HTML.
import { clinicDateD45 } from '@/shared/lib/clinic-time';
import type { ReferralStatus } from '@/types/assessment-api';
import type { PatientReferralRow } from '../services/use-patient-referrals';

export interface ReferralCardView {
  referrer?: string;
  /** The date the referral was created, on the clinic's calendar ("12 Oct 2026"). */
  date?: string;
  reason?: string;
  note?: string;
  status?: { label: string; semantic: 'info' | 'primary' | 'success' | 'neutral' };
}

const STATUS_VIEW: Partial<Record<ReferralStatus, NonNullable<ReferralCardView['status']>>> = {
  sent: { label: 'Sent', semantic: 'info' },
  accepted: { label: 'Accepted', semantic: 'primary' },
  booked: { label: 'Booked', semantic: 'success' },
};

/** Text of a note that may be plain text, a Quill delta (ops array) or markup: its words only. */
export function plainOf(raw: string | null | undefined): string | undefined {
  const text = raw?.trim();
  if (!text) return undefined;
  let words = text;
  if (text.startsWith('[') || text.startsWith('{')) {
    try {
      const parsed = JSON.parse(text) as unknown;
      const ops = Array.isArray(parsed) ? parsed : (parsed as { ops?: unknown })?.ops;
      if (Array.isArray(ops)) {
        words = ops.map((op) => (typeof (op as { insert?: unknown })?.insert === 'string' ? (op as { insert: string }).insert : '')).join('');
      }
    } catch {
      // Prose that starts with a bracket: keep it as it is.
    }
  }
  return words.replace(/<[^>]*>/g, '').trim() || undefined;
}

/** The newest row by createdDate (rows with no readable date go last). The client does not rely on the server's order. */
export function newestReferral(rows: readonly PatientReferralRow[] | null | undefined): PatientReferralRow | undefined {
  const time = (r: PatientReferralRow) => {
    const t = r.createdDate ? Date.parse(r.createdDate) : NaN;
    return Number.isNaN(t) ? -Infinity : t;
  };
  return [...(rows ?? [])].sort((a, b) => time(b) - time(a))[0];
}

export function toReferralCardView(row: PatientReferralRow): ReferralCardView {
  const additional = (row.otherDiagnosis ?? [])
    .map((d) => d.dxCode?.displayName?.trim())
    .filter((n): n is string => !!n)
    .join(', ');
  return {
    referrer: row.referrerName?.trim() || undefined,
    date: clinicDateD45(row.createdDate),
    reason: row.reasonDisplay?.trim() || additional || undefined,
    note: plainOf(row.notePlainText) ?? plainOf(row.notes),
    status: row.status ? STATUS_VIEW[row.status] : undefined,
  };
}
